/**
 * Extra services whose argument values can be typed into the delivery form.
 *
 * Every other service keeps the legacy behaviour — a plain checkbox that submits an empty argument value.
 * Opting a service in here without checking it first would turn its arguments into required form fields
 * (e. g. Czech Post customs content has ten of them), so the list is deliberately explicit.
 *
 * The same list must drive rendering, initial values, submit and validation, otherwise the form ends up
 * with a required field that has no input, or an input whose value is thrown away.
 */
export const ARGUMENT_INPUT_SERVICE_CODES: string[] = [
  'postnummer',
];

/** Argument types filled from the recipient, shown as a hint instead of an input. */
const HINT_ARGUMENT_TYPES: string[] = ['phone', 'email'];

export interface ServiceArgument {
  name: string;
  type: string;
  exampleValue: string;
}

export interface ServiceLike {
  code: string;
  arguments: ServiceArgument[];
}

/** Formik field name holding the value of a single service argument. */
export const getArgumentFieldName = (serviceCode: string, argumentType: string): string => (
  `${serviceCode}__${argumentType}`
);

/** Arguments of the given service that the user fills in by hand, empty for services outside the whitelist. */
export const getInputArguments = (service: ServiceLike): ServiceArgument[] => {
  if (!ARGUMENT_INPUT_SERVICE_CODES.includes(service.code)) {
    return [];
  }

  return (service.arguments || []).filter((argument) => !HINT_ARGUMENT_TYPES.includes(argument.type));
};

/** All services of an agent that have at least one argument to fill in. */
export const getServicesWithArgumentInputs = (services: ServiceLike[]): ServiceLike[] => (
  (services || []).filter((service) => getInputArguments(service).length > 0)
);
